"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import type { PostHog } from "posthog-js";

import { loadPosthog, whenIdleAfterLoad } from "../../instrumentation-client";

const SECTION_THRESHOLD = 0.5;
const MAX_LABEL_LENGTH = 120;

function capture(
  posthog: PostHog,
  event: string,
  properties: Record<string, unknown>,
) {
  if (posthog.has_opted_out_capturing?.()) return;
  posthog.capture(event, properties);
}

function normalizeText(value: string | null | undefined) {
  if (!value) return undefined;
  const trimmed = value.replace(/\s+/g, " ").trim();
  if (!trimmed) return undefined;
  return trimmed.length > MAX_LABEL_LENGTH
    ? `${trimmed.slice(0, MAX_LABEL_LENGTH - 1)}…`
    : trimmed;
}

function cleanProps<T extends Record<string, unknown>>(props: T) {
  return Object.fromEntries(
    Object.entries(props).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
}

function extractSectionProps(element: HTMLElement) {
  const heading = element.querySelector("h1, h2, h3, h4, h5, h6");
  const label =
    normalizeText(element.getAttribute("data-analytics-label")) ??
    normalizeText(element.getAttribute("aria-label")) ??
    normalizeText(heading?.textContent ?? undefined) ??
    (element.id ? normalizeText(element.id) : undefined);

  return cleanProps({
    section_id: element.id || undefined,
    section_label: label,
  });
}

// Module scope on purpose. The effect is keyed on pathname, so anything the
// observer banked before the idle callback fired used to be discarded the
// moment the visitor navigated — which is exactly the slow-load case the
// buffering exists for. Surviving the remount is the whole point.
let posthogHandle: PostHog | null = null;
// Set once the load settles without a client: no key, a webdriver session, a
// blocked import, or a throw. Nothing will ever drain the buffer after that,
// so buffering has to stop rather than grow for the rest of the session.
let posthogUnavailable = false;
const pendingSectionViews: Array<Record<string, unknown>> = [];

const stopBufferingSectionViews = () => {
  posthogUnavailable = true;
  pendingSectionViews.length = 0;
};

const flushPendingSectionViews = (posthog: PostHog) => {
  for (const props of pendingSectionViews.splice(0)) {
    capture(posthog, "section_viewed", props);
  }
};

export function AnalyticsTracker() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === "undefined" || window.self !== window.top) return;

    const seenSections = new WeakSet<Element>();
    const observedSections = new Set<Element>();

    // Only the PostHog import is idle-gated; observation starts immediately.
    // IntersectionObserver reports the sections intersecting when observation
    // starts, which is not the same as the sections the visitor has already
    // seen: on a slow load they can scroll past several before the load event
    // and the idle callback run, and those would never be reported at all.
    // Watching from the first commit and buffering costs no bundle and no
    // network, and keeps section_viewed complete.
    const recordSection = (target: HTMLElement) => {
      const props = {
        current_url: window.location.href,
        ...extractSectionProps(target),
      };
      if (posthogHandle) {
        capture(posthogHandle, "section_viewed", props);
      } else if (!posthogUnavailable) {
        pendingSectionViews.push(props);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (
            !entry.isIntersecting ||
            entry.intersectionRatio < SECTION_THRESHOLD
          )
            continue;
          const target = entry.target as HTMLElement;
          if (seenSections.has(target)) continue;
          seenSections.add(target);
          recordSection(target);
        }
      },
      { threshold: SECTION_THRESHOLD },
    );

    const observeSection = (section: Element) => {
      if (!(section instanceof HTMLElement)) return;
      if (observedSections.has(section)) return;
      observedSections.add(section);
      observer.observe(section);
    };

    document.querySelectorAll("section").forEach((section) => {
      observeSection(section);
    });

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.tagName === "SECTION") {
            observeSection(node);
          }
          node.querySelectorAll?.("section").forEach((section) => {
            observeSection(section);
          });
        });
      }
    });

    if (document.body) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    // Loading the analytics bundle during hydration costs real main-thread
    // time on mobile, and nothing here needs it before the page is loaded and
    // idle. Whatever the observer banked in the meantime is flushed on arrival.
    // Deliberately not cancelled on unmount: the handle and the buffer outlive
    // this effect, so a navigation mid-flight must still resolve and flush
    // rather than drop what was banked. Caught for the same reason the session
    // recorder is: a throw here would otherwise be an unhandled rejection.
    void whenIdleAfterLoad()
      .then(() => loadPosthog())
      .then((loaded) => {
        if (!loaded) {
          stopBufferingSectionViews();
          return;
        }
        posthogHandle = loaded;
        flushPendingSectionViews(loaded);
      })
      .catch(() => {
        stopBufferingSectionViews();
      });

    return () => {
      mutationObserver.disconnect();
      observer.disconnect();
      observedSections.clear();
    };
  }, [pathname]);

  return null;
}
